import { EditorConsumer, EditorContent, EditorContext, EditorOptions, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Indent from "./Toolbar/CustomIndent";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign, { TextAlignOptions } from "@tiptap/extension-text-align";
import ChangeLineHeight from "./Toolbar/ChangeLineHeight";
import { ReactNode, useEffect } from "react";
import { CharacterCount } from "@tiptap/extension-character-count";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Box, SxProps } from "@mui/material";
import { Table } from "@tiptap/extension-table";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
import { TableWrapper } from "./Table/TableWrapper";

export type LegalbirdTipTapEditorProviderProps = {
  children: ReactNode;
  onUpdate?: EditorOptions["onUpdate"];
  placeholder?: string;
  defaultAlignment?: TextAlignOptions["defaultAlignment"];
  editable?: boolean;
  initialContent?: string;
};

const LegalbirdTipTapEditorProvider = ({
  children,
  onUpdate,
  placeholder,
  defaultAlignment,
  editable = true,
  initialContent,
}: LegalbirdTipTapEditorProviderProps) => {
  const extensions = [
    StarterKit,
    Indent,
    Underline,
    Link.configure({
      autolink: true,
    }),
    TextAlign.configure({
      types: ["paragraph"],
      defaultAlignment: defaultAlignment,
    }),
    ChangeLineHeight,
    CharacterCount.configure(),
    Placeholder.configure({ placeholder: placeholder || "" }),
    TableWrapper,
    Table.configure({
      resizable: true,
    }),
    TableRow,
    TableHeader,
    TableCell,
  ];

  const editor = useEditor({
    extensions,
    editable: editable,
    content: initialContent,
    editorProps: {
      handleKeyDown: function (_, event) {
        if (event.key === "Tab") {
          event.preventDefault();
        }
      },
      attributes: {
        spellCheck: "true",
      },
    },
    onUpdate: onUpdate,
  });

  useEffect(() => {
    //setEditable emits an editor "update" event even when the value did not change (tiptap emitUpdate
    //defaults to true), which triggered a draft autosave right on drawer open — only call it on real changes
    if (editor && editor.isEditable !== editable) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  if (!editor) {
    return null;
  }

  return <EditorContext.Provider value={{ editor }}>{children}</EditorContext.Provider>;
};

export const LegalbirdTipTapEditorContent = ({ sx }: { sx?: SxProps }) => (
  <Box sx={sx}>
    <EditorConsumer>{({ editor: currentEditor }) => <EditorContent editor={currentEditor} />}</EditorConsumer>
  </Box>
);

export default LegalbirdTipTapEditorProvider;
